import { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { getConfig } from '../configs/config'
import { loginRequest } from '../authConfig'
import { InteractionRequiredAuthError } from '@azure/msal-browser'

// Thrown by fetchData only when a caller opts in via params.throwOnHttpError, so existing
// callers that rely on the parsed body (even for error responses) stay unaffected.
export class HttpError extends Error {
  status: number
  constructor(status: number) {
    super(`Request failed: ${status}`)
    this.status = status
  }
}

export const useFetch = (app = 'inv'): any => {
  const { instance, accounts } = useMsal()
  const config = getConfig()
  const [isLoading, setLoader] = useState(false)

  const getScopeForApp = (app: string) => {
    return app === 'nexus' || app === 'semanticdata'
      ? config.REACT_NEXUS_AZURE_SCOPE
      : config.REACT_APP_AZURE_SCOPE
  }

  const fetchData = async (url: string, params: any) => {
    setLoader(true)
    try {
      // Build the auth request on demand so the sensitive MSAL account info
      // is never retained outside this function's scope.
      const request = {
        ...loginRequest,
        scopes: [getScopeForApp(app)],
        account: accounts[0]
      }

      const msalResponse = await instance.acquireTokenSilent(request).catch((e: any) => {
        if (e instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(request)
        }
      })

      const options = {
        method: 'get',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${msalResponse?.accessToken}`
        }
      }

      let endpoint = url

      if (!endpoint.startsWith('https')) {
        const baseUrl =
          app === 'nexus'
            ? config?.REACT_NEXUS_API_URL
            : app === 'semanticdata'
            ? config?.REACT_SEMANTIC_API_URL
            : config?.REACT_APP_API_URL
        endpoint = `${baseUrl}${url}`
      }

      const res = await fetch(endpoint, options)

      // Opt-in: surface HTTP error statuses (e.g. 403/404) so callers can react to them,
      // instead of silently parsing an error body as if it were a successful response.
      if (params?.throwOnHttpError && !res.ok) {
        setLoader(false)
        throw new HttpError(res.status)
      }

      let data = undefined
      if (params?.isBlob) {
        data = await res.blob()
      } else {
        data = await res.json()
      }
      setLoader(false)
      return data
    } catch (error) {
      setLoader(false)
      if (error instanceof HttpError) throw error
      return error
    }
  }

  return [isLoading, fetchData] as const
}
